import { 
    getExerciseSets, 
    saveExerciseSets, 
    getAllExerciseSets, 
    saveAllExerciseSets, 
    getExerciseLog, 
    saveExerciseLog 
} from "../utils/localStorageUtils";
import { muscleGroups } from "../data/muscleGroups";
import { getAllExercisesCatalog } from "./customExerciseService";
import { exercises as builtInExercises } from "../data/exercises/index";

function hasValidWeight(weight) {
    if (weight === null || weight === undefined || weight === "") {
        return false;
    }

    const parsedWeight = parseFloat(weight);
    return !Number.isNaN(parsedWeight) && parsedWeight >= 0;
}

function hasValidReps(reps) {
    if (reps === null || reps === undefined || reps === "") {
        return false;
    }

    const parsedReps = parseInt(reps, 10);
    return !Number.isNaN(parsedReps) && parsedReps > 0;
}

function hasValidTime(time) {
    if (time === null || time === undefined || time === "") {
        return false;
    }

    const parsedTime = parseFloat(time);
    return !Number.isNaN(parsedTime) && parsedTime > 0;
}

/**
 * Determines whether an exercise tracks strength (weight + reps) or cardio (time + intensity).
 * @param {object|string} exerciseOrId Exercise object or exercise identifier.
 * @returns {"cardio" | "strength"} Metric tracking type.
 */
export function getExerciseMetricType(exerciseOrId) {
    if (!exerciseOrId) {
        return "strength";
    }

    let exercise = exerciseOrId;
    const exerciseId = typeof exerciseOrId === "string" ? exerciseOrId : exerciseOrId?.id;

    if (typeof exerciseOrId === "string") {
        const catalog = getAllExercisesCatalog();
        exercise = catalog.find((e) => e.id === exerciseOrId) || { id: exerciseOrId };
    }

    if (exercise?.metricType === "cardio") {
        return "cardio";
    }

    const equip = String(exercise?.equipment || "").toLowerCase();
    if (
        equip === "cardio machine" ||
        equip === "кардіотренажер" ||
        equip.includes("cardio") ||
        equip.includes("кардіо")
    ) {
        return "cardio";
    }

    if (exerciseId && Array.isArray(builtInExercises)) {
        const canonical = builtInExercises.find((e) => e.id === exerciseId);
        if (canonical?.equipment === "Cardio Machine" || canonical?.metricType === "cardio") {
            return "cardio";
        }
    }

    return "strength";
}

function filterCompleteSets(sets, exerciseOrId = null) {
    const metricType = exerciseOrId ? getExerciseMetricType(exerciseOrId) : null;

    return sets.filter((set) => {
        if (!set || typeof set !== "object") {
            return false;
        }

        // Support planned items
        if (set.type === "planned") {
            if (metricType === "cardio") {
                return hasValidTime(set.time);
            }
            if (hasValidTime(set.time)) {
                return true;
            }
            return hasValidWeight(set.weight) && hasValidReps(set.reps);
        }

        // Cardio interval
        if (hasValidTime(set.time)) {
            return true;
        }

        // Strength set (reject for cardio exercises)
        if (metricType !== "cardio") {
            return hasValidWeight(set.weight) && hasValidReps(set.reps);
        }

        return false;
    });
}


/**
 * Gets sets for an exercise on a specific date.
 * @param {string} date Date in YYYY-MM-DD format.
 * @param {string} exerciseId Exercise identifier.
 * @returns {Array} Sets for the exercise.
 */
export function getSetsByExercise(date, exerciseId) {
    const log = getExerciseLog();
    const todayLog = log[date] || {};
    return todayLog[exerciseId] || [];
}

/**
 * Gets the most recent historical sets for an exercise before a date.
 * @param {string} exerciseId Exercise identifier.
 * @param {string} beforeDate Date boundary in YYYY-MM-DD format (exclusive).
 * @param {number} limit Maximum number of historical entries to return.
 * @returns {Array<{date: string, weight: string|number, reps: string|number}>}
 */
export function getPastSetsByExercise(exerciseId, beforeDate, limit = 10) {
    const log = getExerciseLog();
    const isDateKey = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

    const dates = Object.keys(log)
        .filter((date) => isDateKey(date) && date < beforeDate)
        .sort((a, b) => b.localeCompare(a));

    const pastSets = [];

    for (const date of dates) {
        const sets = log[date]?.[exerciseId];
        if (!Array.isArray(sets) || sets.length === 0) {
            continue;
        }

        for (const set of sets) {
            if (hasValidTime(set?.time)) {
                pastSets.push({
                    date,
                    time: set.time,
                    rpe: set.rpe || "2",
                });

                if (pastSets.length >= limit) {
                    return pastSets;
                }
                continue;
            }

            const hasReps = hasValidReps(set?.reps);
            const hasWeight = hasValidWeight(set?.weight) || set?.weight === "";
            if (!hasReps || !hasWeight) {
                continue;
            }

            pastSets.push({
                date,
                weight: set.weight,
                reps: set.reps,
            });

            if (pastSets.length >= limit) {
                return pastSets;
            }
        }
    }

    return pastSets;
}

/**
 * Updates sets for an exercise on a specific date.
 * @param {string} date Date in YYYY-MM-DD format.
 * @param {string} exerciseId Exercise identifier.
 * @param {Array} sets Array of sets.
 * @param {string} originalDate Original date in YYYY-MM-DD format (if updating from a different date).
 * @returns {boolean} True if update was successful, false otherwise.
 */
export function updateSetsByExercise(date, exerciseId, sets, originalDate = null) {
    try {
        const log = getExerciseLog();
        const dateLog = log[date] || {};
        const completeSets = filterCompleteSets(sets, exerciseId);

        // If we're updating from a different date, remove the exercise from the original date
        if (originalDate && originalDate !== date && log[originalDate]) {
            const originalDateLog = log[originalDate];
            if (originalDateLog[exerciseId]) {
                delete originalDateLog[exerciseId];
                log[originalDate] = originalDateLog;
                
                // If the original date log is now empty, consider removing it
                if (Object.keys(originalDateLog).length === 0) {
                    // Optionally delete the date entry if it's empty
                    // delete log[originalDate];
                }
            }
        }

        // Update the exercise on the new date
        if (completeSets.length > 0) {
            dateLog[exerciseId] = completeSets; // Save only completed sets
        } else {
            delete dateLog[exerciseId]; // Remove the exercise if there are no completed sets
        }

        log[date] = dateLog;
        const saveResult = saveExerciseLog(log);
        
        if (!saveResult) {
            console.error(`Failed to save exercise sets for ${exerciseId} on ${date}`);
        }
        
        return saveResult;
    } catch (error) {
        console.error(`Error updating sets for exercise ${exerciseId} on ${date}:`, error);
        return false;
    }
}

/**
 * Checks if there is at least one completed set.
 * @param {Array} sets Array of sets.
 * @param {string|object} [exerciseOrId] Exercise ID or exercise object.
 * @returns {boolean} True if there are completed sets.
 */
export function hasCompleteSets(sets, exerciseOrId = null) {
    return filterCompleteSets(sets, exerciseOrId).length > 0;
}

/**
 * Initializes the list of sets for an exercise.
 * @param {string} exerciseId Exercise ID.
 * @returns {Array} List of sets.
 */
export function initializeExerciseSets(exerciseId) {
    const savedSets = getExerciseSets(exerciseId);

    // Add an empty set if the last one is filled
    if (savedSets.length > 0) {
        const lastSet = savedSets[savedSets.length - 1];
        if (hasValidWeight(lastSet.weight) && hasValidReps(lastSet.reps)) {
            return [...savedSets, { weight: "", reps: "" }];
        }
    }

    return savedSets.length > 0 ? savedSets : [{ weight: "", reps: "" }];
}

/**
 * Updates the list of sets and saves them to localStorage.
 * @param {string} exerciseId Exercise ID.
 * @param {Array} sets List of sets.
 * @returns {boolean} True if update was successful, false otherwise.
 */
export function updateExerciseSets(exerciseId, sets) {
    try {
        const completeSets = filterCompleteSets(sets, exerciseId);
        const saveResult = saveExerciseSets(exerciseId, completeSets);
        
        if (!saveResult) {
            console.error(`Failed to save sets for exercise ${exerciseId}`);
        }
        
        return saveResult;
    } catch (error) {
        console.error(`Error updating sets for exercise ${exerciseId}:`, error);
        return false;
    }
}

/**
 * Checks if the last set is complete.
 * @param {Array} sets List of sets.
 * @param {number} index Index of the set.
 * @param {string|object} [exerciseOrId] Exercise ID or exercise object.
 * @returns {boolean} Whether the set is complete.
 */
export function isLastSetComplete(sets, index, exerciseOrId = null) {
    const isLastSet = index === sets.length - 1;
    const set = sets[index] || {};
    const metricType = exerciseOrId ? getExerciseMetricType(exerciseOrId) : null;
    const isSetComplete = metricType === "cardio"
        ? hasValidTime(set.time)
        : hasValidWeight(set.weight) && hasValidReps(set.reps);
    return isLastSet && isSetComplete;
}

/**
 * Returns a muscle group object by its ID.
 * @param {string} muscleGroupId Muscle group ID.
 * @returns {Object|null} Muscle group object or null.
 */
export function getMuscleGroupById(muscleGroupId) {
    return muscleGroups.find((mg) => mg.id === muscleGroupId) || null;
}

/**
 * Filters exercises by priority (primary/secondary) and muscle group.
 * @param {string} muscleGroupId Muscle group ID.
 * @param {string} priority Priority ("primary" or "secondary").
 * @returns {Array} Array of exercises.
 */
export function getExercisesByMuscleGroup(muscleGroupId, priority) {
    return getAllExercisesCatalog().filter((exercise) =>
        exercise.muscleGroups?.some(
            (mg) =>
                mg.group &&
                typeof mg.group === "string" &&
                mg.group.toLowerCase() === muscleGroupId.toLowerCase() &&
                mg.priority === priority
        )
    );
}

/**
 * Gets a list of exercises with recorded sets from localStorage.
 * @returns {Array} List of exercises.
 */
export function getLoggedExercises() {
    const allExercisesSets = getAllExerciseSets();
    return Object.keys(allExercisesSets)
        .map((exerciseId) => ({
            exerciseId,
            sets: allExercisesSets[exerciseId],
        }))
        .filter((exercise) => exercise.sets.length > 0);
}

/**
 * Deletes an exercise by its ID.
 * @param {string} exerciseId Exercise ID.
 * @param {Array} loggedExercises List of exercises.
 * @returns {Object} Object containing updated list of exercises and success status.
 */
export function deleteExercise(exerciseId, loggedExercises) {
    try {
        const updatedExercises = loggedExercises.filter((ex) => ex.exerciseId !== exerciseId);

        const allExercisesSets = {};
        updatedExercises.forEach((ex) => {
            allExercisesSets[ex.exerciseId] = ex.sets;
        });

        const saveResult = saveAllExerciseSets(allExercisesSets);
        
        if (!saveResult) {
            console.error(`Failed to save updated exercise list after deleting ${exerciseId}`);
        }
        
        return { 
            exercises: updatedExercises, 
            success: saveResult 
        };
    } catch (error) {
        console.error(`Error deleting exercise ${exerciseId}:`, error);
        return { 
            exercises: loggedExercises, 
            success: false 
        };
    }
}

export function getAllExercises() {
    return getAllExercisesCatalog();
}

/**
 * Calculates the number of primary and secondary exercises for each muscle group.
 * @param {Array} loggedExercises List of logged exercises.
 * @returns {Object} Object with exercise counts.
 */
export function calculateMuscleGroupCounts(loggedExercises) {
    const counts = {};
    muscleGroups.forEach((group) => {
        counts[group.id] = {
            primary: 0,
            secondary: 0,
        };
    });

    loggedExercises.forEach(({ exerciseId }) => {
        const exercise = getAllExercisesCatalog().find((ex) => ex.id === exerciseId);
        if (exercise) {
            exercise.muscleGroups.forEach((mg) => {
                const groupId = mg.group;
                if (counts[groupId]) {
                    if (mg.priority === "primary") {
                        counts[groupId].primary += 1;
                    } else if (mg.priority === "secondary") {
                        counts[groupId].secondary += 1;
                    }
                }
            });
        }
    });

    return counts;
}





